import { useTranslation } from "@/i18n/LanguageContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StarRating from "@/components/StarRating";
import { CustomPuzzleWalletIndicator } from "./CustomPuzzleWalletIndicator";
import { CustomPuzzle, User } from "../types";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";

interface SolvedPuzzleCardProps {
  puzzle: CustomPuzzle;
  user: User | undefined;
  onNavigate: (path: string) => void;
  onOpenRatingDialog: (puzzleId: number) => void;
  onOpenAnswerDialog: (puzzleId: number) => void;
}

export function SolvedPuzzleCard({ 
  puzzle, 
  user, 
  onNavigate, 
  onOpenRatingDialog,
  onOpenAnswerDialog
}: SolvedPuzzleCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const handleClick = () => {
    onNavigate(`/puzzles/custom/${puzzle.id}`);
  };
  
  const handleRateClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenRatingDialog(puzzle.id);
  };
  
  const handleAnswerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenAnswerDialog(puzzle.id);
  };
  
  const handleCopyAddress = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(puzzle.address);
    toast({ title: t("desafios_cacatais.address_copied") });
  };

  const canAddAnswer = user && 
    (puzzle.user.id === user.id || (puzzle.solvedByUser && puzzle.solvedByUser.id === user.id)) && 
    (!puzzle.answers || puzzle.answers.length < 2);

  return (
    <Card 
      key={puzzle.id} 
      className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800/90 transition-all shadow-lg border-l-4 border-l-green-500 cursor-pointer flex flex-col h-full"
      onClick={handleClick}
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-xl break-words">{puzzle.title}</CardTitle>
        <CardDescription className="text-zinc-400 text-sm">
          {t("desafios_cacatais.prize", { amount: parseFloat(puzzle.prize).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col pt-0">
        {puzzle.imageUrl && (
          <div className="mb-3 rounded-md overflow-hidden">
            <img 
              src={puzzle.imageUrl} 
              alt={puzzle.title}
              className="w-full aspect-[16/9] object-cover"
              loading="lazy"
            />
          </div>
        )}
        <p className="text-zinc-300 mb-3 line-clamp-3 whitespace-pre-line text-sm">{puzzle.description}</p>
        <p className="text-xs text-zinc-500 mb-3">
          {t("desafios_cacatais.created", { date: new Date(puzzle.createdAt).toLocaleDateString() })}
        </p>
        <div className="flex items-center mb-3 bg-zinc-800/70 p-2 rounded-md">
          {puzzle.user.avatarUrl ? (
            <img 
              src={puzzle.user.avatarUrl} 
              alt="Creator" 
              className="w-6 h-6 rounded-full mr-2"
              loading="lazy"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center mr-2">
              <span className="text-xs text-white">
                {puzzle.user.displayName?.charAt(0) || puzzle.user.email.charAt(0)}
              </span>
            </div>
          )}
          <Link 
            href={`/users/${puzzle.user.displayName ? puzzle.user.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : puzzle.user.id}`}
            className="text-xs text-zinc-300 truncate hover:text-blue-400 hover:underline transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {t("desafios_cacatais.created_by", { name: puzzle.user.displayName || puzzle.user.email.split('@')[0] })}
          </Link>
        </div>
        <p className="text-xs text-zinc-500 mb-3 truncate">
          {t("desafios_cacatais.address", { address: puzzle.address })}
        </p>
        <CustomPuzzleWalletIndicator address={puzzle.address} />
        
        {/* Rating display for solved puzzles */}
        <div className="flex items-center justify-between mt-2 mb-3">
          <StarRating 
            value={puzzle.averageRating || 0} 
            totalRatings={puzzle.totalRatings} 
            readOnly 
            showCount 
            size={16}
          />
          
          <Button 
            variant="outline" 
            size="sm" 
            className="ml-2 text-xs" 
            onClick={handleRateClick}
          >
            {t("desafios_cacatais.rate_button", { fallback: "Rate" })}
          </Button>
        </div>
        
        {puzzle.solvedByUser && (
          <div className="flex items-center mb-3 bg-zinc-800/70 p-2 rounded-md">
            {puzzle.solvedByUser.avatarUrl ? (
              <img 
                src={puzzle.solvedByUser.avatarUrl} 
                alt="Solver" 
                className="w-6 h-6 rounded-full mr-2"
                loading="lazy"
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center mr-2">
                <span className="text-xs text-white">
                  {puzzle.solvedByUser.displayName?.charAt(0) || puzzle.solvedByUser.email.charAt(0)}
                </span>
              </div>
            )}
            <Link 
              href={`/users/${puzzle.solvedByUser.displayName ? puzzle.solvedByUser.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : puzzle.solvedByUser.id}`}
              className="text-xs text-zinc-300 truncate hover:text-blue-400 hover:underline transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {t("desafios_cacatais.solved_by", { name: puzzle.solvedByUser.displayName || puzzle.solvedByUser.email.split('@')[0] })}
            </Link>
          </div>
        )}
        
        {/* Display answers if available */}
        {puzzle.answers && puzzle.answers.length > 0 && (
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-zinc-300 mb-2">
              {t("desafios_cacatais.answers.title", { fallback: "Answers" })} ({puzzle.answers.length})
            </h3>
            <div className="space-y-2">
              {puzzle.answers.map((answer) => (
                <div key={answer.id} className="bg-zinc-800/70 p-2 rounded-md">
                  <div className="flex items-center mb-1">
                    {answer.user.avatarUrl ? (
                      <img 
                        src={answer.user.avatarUrl} 
                        alt={answer.user.displayName || answer.user.email}
                        className="w-5 h-5 rounded-full mr-2"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center mr-2">
                        <span className="text-[10px] text-white">
                          {answer.user.displayName?.charAt(0) || answer.user.email.charAt(0)}
                        </span>
                      </div>
                    )}
                    <Link 
                      href={`/users/${answer.user.displayName ? answer.user.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : answer.user.id}`}
                      className="text-xs text-zinc-300 truncate max-w-[100px] sm:max-w-none hover:text-blue-400 hover:underline transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {answer.user.displayName || answer.user.email.split('@')[0]}
                    </Link>
                    <span className="text-xs text-zinc-500 ml-auto">
                      {new Date(answer.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-300 whitespace-pre-line line-clamp-3">{answer.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex flex-wrap gap-2 mt-auto">
          <Button 
            variant="outline" 
            size="sm"
            className="text-xs flex-1 min-w-0"
            onClick={handleCopyAddress}
          >
            {t("desafios_cacatais.copy_address")}
          </Button>
          {canAddAnswer && (
            <Button 
              variant="outline"
              size="sm"
              className="text-xs flex-1 min-w-0"
              onClick={handleAnswerClick}
            >
              {t("desafios_cacatais.add_answer", { fallback: "Add Answer" })}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
